import {default as cx} from "classnames"
import {Skeleton} from "primereact/skeleton"
import {useEffect, useState} from "react"
import {Link} from "react-router-dom"
import useSWR from "swr"

import {OrientationReadinessChart} from "../components/orientation/OrientationReadinessChart"
import {OrientationWeekDetails} from "../components/orientation/OrientationWeekDetails"
import {OrientationReadinessColumn} from "../components/orientation/OrientationReadinessColumn"
import {fetcherGet, getJSDate, getLocaleDate, ORIENTATION_STATUS, capitalizePersonName, getShiftedWeeks} from "../utilities/general"

const HomeOrientee = () => {
    useEffect(() => {
        document.title = "Orientation Home"
    }, [])
    const {data: orientee, isLoading} = useSWR("/orientation-details", fetcherGet, {fallbackData: {}})
    const [showDetails, setShowDetails] = useState(false)
    const [visibleWeeks, setVisibleWeeks] = useState(3)
    const weeks = orientee.weeks ?? {}
    const nhsrDisabled =
        new Date() < getJSDate(orientee.start_date) || orientee.orientation_status_id === ORIENTATION_STATUS.PAUSED

    if (!isLoading && Object.keys(orientee).length === 1) {
        return (
            <div className="container d-flex flex-column">
                <div className="card p-8 mt-6">No active orientations found at this time</div>
            </div>
        )
    }

    return (
        <div className="container d-flex flex-column">
            <div className="card p-8 mt-6">
                <div className="card-header px-4">
                    <h3 className="card-title">My Information</h3>
                    <div className="card-toolbar">
                        {nhsrDisabled ? (
                            <span
                                className="btn btn-primary text-nowrap d-md-block disabled"
                                aria-disabled="true"
                                style={{pointerEvents: "none", opacity: 0.5}}
                            >
                                NHSR
                            </span>
                        ) : (
                            <Link
                                to="/surveys/new-hire-status-report-selection"
                                className="btn btn-primary text-nowrap d-md-block"
                            >
                                {orientee.nhsr_submission_status === false && <span>Due: </span>}
                                NHSR
                            </Link>
                        )}
                    </div>
                </div>
                <div className="card-body px-4">
                    {isLoading ? (
                        <Skeleton className="w-100 h-80px mb-3" />
                    ) : (
                        <div className="row row-cols-1 row-cols-lg-2 g-4">
                            <div className="col">
                                <div className="d-flex flex-wrap fw-semibold fs-6 mb-md-0 mb-4 pe-2 gap-2">
                                    <span className="d-flex align-items-center me-5 mb-2">
                                        <i className={"fas text-primary fs-6 me-2 fa-hospital"}></i>
                                        {orientee.unit_name ? orientee.unit_name : "No Info"}
                                    </span>
                                    <span className="d-flex align-items-center me-5 mb-2">
                                        <i className={"fas text-primary fs-6 me-2 fa-user-nurse"}></i>
                                        {orientee.position_name ? orientee.position_name : "No Info"}
                                    </span>
                                    {orientee.preceptors && orientee.preceptors.length > 0 && (
                                        <span className="d-flex align-items-center me-5 mb-2">
                                            <i className={"fas text-primary fs-6 me-2 fa-chalkboard-user"}></i>
                                            Preceptor(s):{" "}
                                            {orientee.preceptors
                                                ? orientee.preceptors.map((p) => capitalizePersonName(p.preceptor_name)).join(", ")
                                                : "No Info"}
                                        </span>
                                    )}
                                    <span className="d-flex align-items-center me-5 mb-2">
                                        <i className={"fas text-primary fs-6 me-2 fa-clock-rotate-left"}></i>
                                        Prior Exp: {orientee.experience_level_name ? orientee.experience_level_name : "No Info"}
                                    </span>
                                    <span className="d-flex align-items-center me-5 mb-2">
                                        <i className={"fas text-primary fs-6 me-2 fa-graduation-cap"}></i>
                                        {orientee.nursing_program_completed
                                            ? `${orientee.nursing_program_completed} ${getLocaleDate(orientee.nurse_graduate_board_date)}`
                                            : "No Info"}
                                    </span>
                                    <span className="d-flex align-items-center me-5 mb-2">
                                        <i className={"fas text-primary fs-6 me-2 fa-flag"}></i>
                                        Hired: {orientee.hire_date ? getLocaleDate(orientee.hire_date) : "No Info"}
                                    </span>
                                    <span className="d-flex align-items-center me-5 mb-2">
                                        <i className={"fas text-primary fs-6 me-2 fa-flag"}></i>
                                        Started: {orientee.start_date ? getLocaleDate(orientee.start_date) : "No Info"}
                                    </span>

                                    <span className="d-flex align-items-center me-5 mb-2">
                                        <i className="fas text-primary fs-6 me-2 fa-calendar-xmark"></i>
                                        Expected End Date: {orientee.expected_end_date
                                            ? getLocaleDate(orientee.expected_end_date)
                                            : "No Info"}
                                    </span>
                                    <span className="d-flex align-items-center me-5 mb-2">
                                        <i className="fas text-primary fs-6 me-2 fa-calendar-xmark"></i>
                                        Length: {orientee.standard_orientation_length_weeks
                                            ? `${orientee.standard_orientation_length_weeks} weeks`
                                            : "No Info"}
                                        {orientee.educator_shifted_weeks ? (
                                            <span className="ms-2">{getShiftedWeeks(orientee)}</span>
                                        ) : null}
                                    </span>
                                </div>
                            </div>
                            {orientee.current_percent_max_osr_score !== undefined && (
                                <OrientationReadinessColumn percentOSRScore={orientee.current_percent_max_osr_score} />
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="card p-8 mt-6">
                <div className="card-header px-4">
                    <h3 className="card-title">My Weekly Progress</h3>
                    <div className="card-toolbar">
                        {isLoading ? (
                            <Skeleton className="w-100px h-40px mb-3" />
                        ) : (
                            <button
                                className={cx("btn btn-light-primary rotate collapsible", {collapsed: !showDetails})}
                                onClick={() => setShowDetails(!showDetails)}
                            >
                                Details
                                <span className="d-flex flex-center rotate-180 ms-3">
                                    <i className="fas fa-arrow-circle-down fs-3"></i>
                                </span>
                            </button>
                        )}
                    </div>
                </div>
                {showDetails && (
                    <div className="card-body px-4">
                        <OrientationReadinessChart orientee={orientee} />
                        <div className="d-flex flex-column gap-2">
                            {Object.keys(weeks)
                                .sort((a, b) => parseInt(b) - parseInt(a))
                                .slice(0, visibleWeeks)
                                .map((week) => {
                                    const weekData = weeks[week]
                                    return (
                                        <OrientationWeekDetails
                                            key={week}
                                            week={week}
                                            weekData={weekData}
                                            orientation={orientee}
                                            hideTextAnswers
                                        />
                                    )
                                })}
                        </div>
                        {visibleWeeks < Object.keys(weeks).length && (
                            <div className="d-flex flex-column mt-4">
                                <button
                                    className="btn btn-light-primary"
                                    onClick={() => setVisibleWeeks(visibleWeeks + 3)}
                                >
                                    Load More Weeks
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default HomeOrientee
